<template>
  <div class="proxy-management-container">
    <div class="main-content">
      <!-- 服务器信息卡片 -->
      <div class="proxy-card">
        <div class="card-header">
          <h3 class="card-title">{{ $t('proxyMgmt.title') }}</h3>

          <div class="card-actions">
              <el-button type="info" size="small" @click="showLogDialog('socks5')">
                <el-icon><Document /></el-icon>
                {{ $t('proxyMgmt.viewLogs') }}
              </el-button>
          </div>
        </div>
        <div class="card-body">
          <p class="description-text">
            {{ $t('proxyMgmt.description') }}
          </p>
        </div>
      </div>

      <!-- SOCKS5代理配置卡片 -->
      <div class="proxy-card">
        <div class="card-header">
          <h3 class="card-title">{{ $t('proxyMgmt.socks5Proxy') }}</h3>
            <el-button
              type="primary"
              size="small"
              @click="showEditDialog('socks5')"
              :disabled="socks5.switch"
            >
              {{ $t('proxyMgmt.editConfig') }}
            </el-button>
        </div>
        <div class="card-body">
          <div class="table-responsive">
            <table class="proxy-table">
              <thead>
                <tr>
                  <th>{{ $t('proxyMgmt.ipAddress') }}</th>
                  <th>{{ $t('proxyMgmt.port') }}</th>
                  <th>{{ $t('proxyMgmt.username') }}</th>
                  <th>{{ $t('proxyMgmt.password') }}</th>
                  <th>{{ $t('proxyMgmt.status') }}</th>
                  <th>{{ $t('proxyMgmt.operation') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td data-label="IP地址">{{ socks5.ip }}</td>
                  <td data-label="端口号">{{ socks5.port }}</td>
                  <td data-label="用户名">{{ socks5.username || $t('proxyMgmt.noAuth') }}</td>
                  <td data-label="密码">
                    <div class="password-field">
                      <span v-if="showSocks5Password">{{ socks5.password || $t('proxyMgmt.noPassword') }}</span>
                      <span v-else>••••••</span>
                      <el-icon class="eye-icon" @click="showSocks5Password = !showSocks5Password">
                        <View v-if="showSocks5Password" />
                        <Hide v-else />
                      </el-icon>
                    </div>
                  </td>
                  <td data-label="状态">
                    <div :class="['status-badge', getStatusClass(socks5.status)]">
                      {{ formatStatus(socks5.status) }}
                    </div>
                  </td>
                  <td data-label="操作" class="action-cell">
                    <div class="action-buttons">
                      <el-button
                        v-if="socks5.switch"
                        type="warning"
                        size="small"
                        @click="toggleProxy('socks5', false)"
                        :loading="socks5Loading"
                      >
                        {{ $t('proxyMgmt.stop') }}
                      </el-button>
                      <el-button
                        v-else
                        type="success"
                        size="small"
                        @click="toggleProxy('socks5', true)"
                        :loading="socks5Loading"
                      >
                        {{ $t('proxyMgmt.start') }}
                      </el-button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- HTTPS代理配置卡片 -->
      <div class="proxy-card">
        <div class="card-header">
          <h3 class="card-title">{{ $t('proxyMgmt.httpsProxy') }}</h3>
            <el-button
              type="primary"
              size="small"
              @click="showEditDialog('https')"
              :disabled="proxy.switch"
            >
              {{ $t('proxyMgmt.editConfig') }}
            </el-button>
        </div>
        <div class="card-body">
          <div class="table-responsive">
            <table class="proxy-table">
              <thead>
                <tr>
                  <th>{{ $t('proxyMgmt.ipAddress') }}</th>
                  <th>{{ $t('proxyMgmt.port') }}</th>
                  <th>{{ $t('proxyMgmt.username') }}</th>
                  <th>{{ $t('proxyMgmt.password') }}</th>
                  <th>{{ $t('proxyMgmt.status') }}</th>
                  <th>{{ $t('proxyMgmt.operation') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td data-label="IP地址">{{ proxy.ip }}</td>
                  <td data-label="端口号">{{ proxy.port }}</td>
                  <td data-label="用户名">{{ proxy.username || $t('proxyMgmt.noAuth') }}</td>
                  <td data-label="密码">
                    <div class="password-field">
                      <span v-if="showHttpsPassword">{{ proxy.password || $t('proxyMgmt.noPassword') }}</span>
                      <span v-else>••••••</span>
                      <el-icon class="eye-icon" @click="showHttpsPassword = !showHttpsPassword">
                        <View v-if="showHttpsPassword" />
                        <Hide v-else />
                      </el-icon>
                    </div>
                  </td>
                  <td data-label="状态">
                    <div :class="['status-badge', getStatusClass(proxy.status)]">
                      {{ formatStatus(proxy.status) }}
                    </div>
                  </td>
                  <td data-label="操作" class="action-cell">
                    <div class="action-buttons">
                      <el-button
                        v-if="proxy.switch"
                        type="warning"
                        size="small"
                        @click="toggleProxy('https', false)"
                        :loading="httpsLoading"
                      >
                        {{ $t('proxyMgmt.stop') }}
                      </el-button>
                      <el-button
                        v-else
                        type="success"
                        size="small"
                        @click="toggleProxy('https', true)"
                        :loading="httpsLoading"
                      >
                        {{ $t('proxyMgmt.start') }}
                      </el-button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    <!-- 编辑配置对话框 -->
    <el-dialog
      v-model="dialogVisible"
      :title="$t('proxyMgmt.editProxyConfig', { type: dialogType === 'socks5' ? 'SOCKS5' : 'HTTPS' })"
      :width="isMobile ? '95%' : '600px'"
      :before-close="handleCloseDialog"
      :destroy-on-close="true"
      class="proxy-dialog"
    >
      <el-form
        :model="dialogForm"
        :rules="formRules"
        ref="dialogFormRef"
        :label-position="isMobile ? 'top' : 'right'"
        label-width="120px"
      >
        <el-form-item :label="$t('proxyMgmt.ipAddress')" prop="ip">
          <el-input v-model="dialogForm.ip" :placeholder="$t('proxyMgmt.ipPlaceholder')" />
        </el-form-item>

        <el-form-item :label="$t('proxyMgmt.port')" prop="port">
          <el-input
            v-model.number="dialogForm.port"
            type="number"
            :placeholder="$t('proxyMgmt.portPlaceholder')"
          />
        </el-form-item>

        <el-form-item :label="$t('proxyMgmt.username')" prop="username">
          <el-input
            v-model="dialogForm.username"
            :placeholder="$t('proxyMgmt.usernamePlaceholder')"
          />
        </el-form-item>

        <el-form-item :label="$t('proxyMgmt.password')" prop="password">
          <el-input
            v-model="dialogForm.password"
            type="password"
            show-password
            :placeholder="$t('proxyMgmt.passwordPlaceholder')"
          />
        </el-form-item>
      </el-form>

      <template #footer>
        <div class="dialog-footer">
          <el-button @click="dialogVisible = false">{{ $t('proxyMgmt.cancel') }}</el-button>
          <el-button
            type="primary"
            @click="submitProxyConfig"
            :loading="dialogLoading"
          >
            {{ $t('proxyMgmt.save') }}
          </el-button>
        </div>
      </template>
    </el-dialog>

    <!-- 代理日志查看对话框 -->
    <el-dialog
      v-model="logDialogVisible"
      :title="$t('proxyMgmt.runningLogs')"
      :width="isMobile ? '95%' : '900px'"
      :close-on-click-modal="false"
      :destroy-on-close="true"
      class="settings-dialog log-dialog"
    >
      <div class="log-toolbar">
        <div class="log-stats">
          <span>{{ $t('proxyMgmt.totalLogs', { count: logTotal }) }}</span>
          <div class="auto-refresh-control">
            <span class="refresh-label">{{ $t('proxyMgmt.autoRefresh') }}</span>
            <el-switch
              v-model="autoRefreshEnabled"
              :active-value="true"
              :inactive-value="false"
              @change="toggleAutoRefresh"
              size="small"
              active-color="#409eff"
            />
            <span class="refresh-interval" v-if="autoRefreshEnabled">{{ $t('proxyMgmt.refreshInterval') }}</span>
          </div>
        </div>
        <div class="log-actions">
          <el-button
            type="success"
            size="small"
            @click="downloadAllLogs"
            :loading="logDownloading"
            :icon="Download"
          >
            {{ $t('proxyMgmt.downloadAllLogs') }}
          </el-button>
          <el-button
            type="primary"
            size="small"
            @click="refreshLogs"
            :loading="logLoading"
            :icon="Refresh"
          >
            {{ $t('proxyMgmt.refreshNow') }}
          </el-button>
          <el-button
            type="danger"
            size="small"
            @click="clearLogs"
            :loading="logClearing"
          >
            {{ $t('proxyMgmt.clearLogs') }}
          </el-button>
        </div>
      </div>

      <div class="log-content" ref="logContentRef">
        <div v-if="logsData.length === 0" class="empty-log">
          <el-icon><InfoFilled /></el-icon>
          <p>{{ $t('proxyMgmt.noLogs') }}</p>
        </div>
        <div v-else class="log-list">
          <div
            v-for="(log, index) in logsData"
            :key="index"
            class="log-item"
            :class="`log-level-${log.Level.toLowerCase()}`"
          >
            <span class="log-time">{{ log.Time }}</span>
            <span class="log-level" :class="`level-${log.Level.toLowerCase()}`">
              [{{ log.Level }}]
            </span>
            <span class="log-message">{{ log.Content }}</span>
          </div>
        </div>
      </div>

      <div class="log-pagination" v-if="logTotal > 0">
        <el-pagination
          v-model:current-page="logCurrentPage"
          v-model:page-size="logPageSize"
          :page-sizes="[20, 50, 100, 200]"
          :total="logTotal"
          layout="total, sizes, prev, pager, next, jumper"
          @size-change="handleLogSizeChange"
          @current-change="handleLogPageChange"
          small
        />
      </div>

      <template #footer>
        <div class="dialog-footer">
          <el-button @click="logDialogVisible = false">{{ $t('proxyMgmt.close') }}</el-button>
        </div>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { ref, onMounted, onUnmounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { ElMessage, ElMessageBox } from 'element-plus'
import { View, Hide, Document, InfoFilled } from '@element-plus/icons-vue'
import { API_BASE_URL } from "@/config";
import { useRouter } from 'vue-router'

export default {
  name: 'ProxyManagement',
  components: {
    View,
    Hide,
    Document,
    InfoFilled
  },
  setup() {
    const { t: i18n_t } = useI18n()
    const router = useRouter()
    
    // 用户信息
    const isAdmin = ref(false)
    const isLogin = ref(false)
    const loginUsername = ref('')

    const isMobile = ref(false)
    
    // 检测移动端
    const checkMobile = () => {
      isMobile.value = window.innerWidth < 768
    }
    
    // 代理数据
    const socks5 = ref({
      index: 0,
      ip: '0.0.0.0',
      port: 0,
      username: '',
      password: '',
      switch: false,
      status: 'not running'
    })
    
    const proxy = ref({
      index: 0,
      ip: '0.0.0.0',
      port: 0,
      username: '',
      password: '',
      switch: false,
      status: 'not running'
    })
    
    // 密码显示状态
    const showSocks5Password = ref(false)
    const showHttpsPassword = ref(false)
    
    // 对话框相关
    const dialogVisible = ref(false)
    const dialogType = ref('socks5') // 'socks5' or 'https'
    const dialogForm = ref({
      ip: '',
      port: '',
      username: '',
      password: ''
    })
    const dialogFormRef = ref(null)
    const dialogLoading = ref(false)
    
    // 加载状态
    const socks5Loading = ref(false)
    const httpsLoading = ref(false)
    
    // 日志对话框相关
    const logDialogVisible = ref(false)
    const logDialogType = ref('socks5') // 'socks5' or 'https'
    const logLoading = ref(false)
    const logClearing = ref(false)
    const logDownloading = ref(false)
    const logsData = ref([])
    const logTotal = ref(0)
    const logCurrentPage = ref(1)
    const logPageSize = ref(50)
    const logContentRef = ref(null)
    const autoRefreshEnabled = ref(true) // 默认开启自动刷新
    const logRefreshInterval = ref(null)
    
    // 表单验证规则
    const formRules = {
      ip: [
        { required: true, message: i18n_t('proxyMgmt.ipRequired'), trigger: 'blur' },
        {
          pattern: /^(\d{1,3}\.){3}\d{1,3}$/,
          message: i18n_t('proxyMgmt.ipFormatError'),
          trigger: 'blur'
        }
      ],
      port: [
        { required: true, message: i18n_t('proxyMgmt.portRequired'), trigger: 'blur' },
        {
          type: 'number',
          min: 1,
          max: 65535,
          message: i18n_t('proxyMgmt.portRangeError'),
          trigger: 'blur'
        }
      ],
      username: [
        {
          pattern: /^[a-zA-Z0-9]{0,15}$/,
          message: i18n_t('proxyMgmt.usernameFormatError'),
          trigger: 'blur'
        }
      ],
      password: [
        {
          pattern: /^[a-zA-Z0-9]{0,64}$/,
          message: i18n_t('proxyMgmt.passwordFormatError'),
          trigger: 'blur'
        }
      ]
    }
    
    // 关闭对话框前的确认
    const handleCloseDialog = (done) => {
      if (dialogFormRef.value) {
        ElMessageBox.confirm(i18n_t('proxyMgmt.confirmDiscard'), i18n_t('proxyMgmt.confirm'), {
          confirmButtonText: i18n_t('proxyMgmt.confirm'),
          cancelButtonText: i18n_t('proxyMgmt.cancelText'),
          type: 'warning'
        }).then(() => {
          done()
        }).catch(() => {})
      } else {
        done()
      }
    }

    // 获取状态样式类
    const getStatusClass = (status) => {
      if (!status) return 'stopped'

      const lowerStatus = status.toLowerCase()

      if (lowerStatus.includes('not running')) {
        return 'stopped'
      } else if (lowerStatus.includes('running') || lowerStatus.includes('active')) {
        return 'running'
      } else if (lowerStatus.includes('error') || lowerStatus.includes('fail')) {
        return 'error'
      } else {
        return 'stopped'
      }
    }

    // 格式化状态显示
    const formatStatus = (status) => {
      const statusMap = {
        'not running': i18n_t('proxyMgmt.notRunning'),
        'running': i18n_t('proxyMgmt.running'),
        'stopped': i18n_t('proxyMgmt.stopped'),
        'error': i18n_t('proxyMgmt.error'),
        'not connect': i18n_t('proxyMgmt.notConnected'),
        'success': i18n_t('proxyMgmt.success')
      }
      return statusMap[status] || status
    }
    
    // 显示编辑对话框
    const showEditDialog = (type) => {
      dialogType.value = type
      const sourceData = type === 'socks5' ? socks5.value : proxy.value
      
      dialogForm.value = {
        ip: sourceData.ip,
        port: sourceData.port,
        username: sourceData.username,
        password: sourceData.password
      }
      
      dialogVisible.value = true
    }
    
    // 提交代理配置
    const submitProxyConfig = async () => {
      try {
        await dialogFormRef.value.validate()
        dialogLoading.value = true
        
        const isSocks5 = dialogType.value === 'socks5'
        const endpoint = isSocks5 ? '/update-socks5' : '/update-proxy'
        const proxyData = isSocks5 ? socks5 : proxy
        
        // 准备参数
        const params = new URLSearchParams()
        params.append('username', dialogForm.value.username)
        params.append('password', dialogForm.value.password)
        params.append('port', dialogForm.value.port)
        
        const response = await fetch(`${API_BASE_URL}${endpoint}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          credentials: 'include',
          body: params
        })
        
        if (!response.ok) {
          throw new Error(i18n_t('proxyMgmt.operationFailed'))
        }

        const data = await response.json()
        if (data.errCode !== 0) {
          throw new Error(data.errMsg || i18n_t('proxyMgmt.operationFailed'))
        }

        // 更新本地数据
        Object.assign(proxyData.value, {
          ip: dialogForm.value.ip,
          port: dialogForm.value.port,
          username: dialogForm.value.username,
          password: dialogForm.value.password,
          switch: true, // 更新配置后自动启动
          status: 'running'
        })

        ElMessage.success(i18n_t('proxyMgmt.configUpdateSuccess'))
        dialogVisible.value = false
      } catch (error) {
        console.error(i18n_t('proxyMgmt.configUpdateFailed') + ':', error)
        ElMessage.error(i18n_t('proxyMgmt.configUpdateFailed') + ': ' + (error.message || i18n_t('proxyMgmt.unknownError')))
      } finally {
        dialogLoading.value = false
      }
    }

    // 启动/停止代理
    const toggleProxy = async (type, start) => {
      try {
        const proxyData = type === 'socks5' ? socks5 : proxy
        const loadingRef = type === 'socks5' ? socks5Loading : httpsLoading

        loadingRef.value = true

        const endpoint = start
          ? (type === 'socks5' ? '/start-socks5' : '/start-proxy')
          : (type === 'socks5' ? '/stop-socks5' : '/stop-proxy')

        const method = 'POST'
        const body = start ? null : 'flag=off' // 根据原HTML代码

        const response = await fetch(`${API_BASE_URL}${endpoint}`, {
          method,
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          credentials: 'include',
          body: body
        })

        if (!response.ok) {
          throw new Error(i18n_t('proxyMgmt.operationFailed'))
        }

        const data = await response.json()

        if (data.errCode !== 0) {
          throw new Error(data.errMsg || i18n_t('proxyMgmt.operationFailed'))
        }

        // 更新本地状态
        proxyData.value.switch = start
        proxyData.value.status = start ? 'running' : 'stopped'

        const typeLabel = type === 'socks5' ? 'SOCKS5' : 'HTTPS'
        ElMessage.success(i18n_t(start ? 'proxyMgmt.startSuccess' : 'proxyMgmt.stopSuccess', { type: typeLabel }))
      } catch (error) {
        console.error(i18n_t('proxyMgmt.operationFailed') + ':', error)
        const typeLabel = type === 'socks5' ? 'SOCKS5' : 'HTTPS'
        ElMessage.error(i18n_t(start ? 'proxyMgmt.startFailed' : 'proxyMgmt.stopFailed', { type: typeLabel, error: error.message || i18n_t('proxyMgmt.unknownError') }))
      } finally {
        if (type === 'socks5') {
          socks5Loading.value = false
        } else {
          httpsLoading.value = false
        }
      }
    }

    // 显示日志对话框
    const showLogDialog = (type) => {
      logDialogType.value = type
      logDialogVisible.value = true
      fetchLogs()
      // 开启日志自动刷新（如果开启状态）
      if (autoRefreshEnabled.value) {
        startLogAutoRefresh()
      }
    }

    // 切换自动刷新
    const toggleAutoRefresh = (enabled) => {
      if (enabled) {
        startLogAutoRefresh()
        ElMessage.success(i18n_t('proxyMgmt.autoRefreshEnabled'))
      } else {
        stopLogAutoRefresh()
        ElMessage.info(i18n_t('proxyMgmt.autoRefreshDisabled'))
      }
    }
    
    // 启动日志自动刷新
    const startLogAutoRefresh = () => {
      stopLogAutoRefresh() // 先停止已有的定时器
      logRefreshInterval.value = setInterval(() => {
        if (logDialogVisible.value) {
          fetchLogs(false) // 静默刷新，不显示加载状态
        }
      }, 5000)
    }
    
    // 停止日志自动刷新
    const stopLogAutoRefresh = () => {
      if (logRefreshInterval.value) {
        clearInterval(logRefreshInterval.value)
        logRefreshInterval.value = null
      }
    }
    
    // 获取日志
    const fetchLogs = async (showLoading = true) => {
      try {
        if (showLoading) {
          logLoading.value = true
        }
                
        const params = new URLSearchParams({
          module: 'proxy',
          page: logCurrentPage.value,
          size: logPageSize.value
        })
        
        const response = await fetch(`${API_BASE_URL}/get-moduleLog?${params.toString()}`, {
          credentials: 'include'
        })
        
        if (!response.ok) throw new Error(i18n_t('proxyMgmt.networkError'))

        const data = await response.json()
        if (data.errCode === 1000) {
          ElMessage.error(i18n_t('proxyMgmt.pleaseLoginFirst'))
          router.push('/user-login?callback=proxy-mgmt')
          return
        }

        if (data.errCode !== 0) {
          throw new Error(data.errMsg || i18n_t('proxyMgmt.fetchLogsFailed'))
        }

        logsData.value = data.data || []
        logTotal.value = data.count || 0

        // 只有在非自动刷新（即手动操作）时才滚动到顶部
        if (showLoading && logContentRef.value) {
          logContentRef.value.scrollTop = 0
        }
      } catch (error) {
        console.error(i18n_t('proxyMgmt.fetchLogsFailed') + ':', error)
        if (showLoading) {
          ElMessage.error(i18n_t('proxyMgmt.fetchLogsFailed') + ': ' + (error.message || i18n_t('proxyMgmt.unknownError')))
        }
      } finally {
        if (showLoading) {
          logLoading.value = false
        }
      }
    }

    // 刷新日志
    const refreshLogs = () => {
      fetchLogs(true)
    }

    // 清空日志
    const clearLogs = async () => {
      try {
        const moduleName = logDialogType.value === 'socks5' ? 'SOCKS5' : 'HTTPS'
        await ElMessageBox.confirm(i18n_t('proxyMgmt.confirmClearLogs', { type: moduleName }), i18n_t('proxyMgmt.confirm'), {
          confirmButtonText: i18n_t('proxyMgmt.confirm'),
          cancelButtonText: i18n_t('proxyMgmt.cancelText'),
          type: 'warning'
        })

        logClearing.value = true

        const module = logDialogType.value === 'socks5' ? 'socks5' : 'proxy'

        const params = new URLSearchParams({
          module: module
        })

        const response = await fetch(`${API_BASE_URL}/clear-moduleLog?${params.toString()}`, {
          method: 'DELETE',
          credentials: 'include'
        })

        if (!response.ok) throw new Error(i18n_t('proxyMgmt.networkError'))

        const data = await response.json()
        if (data.errCode === 1000) {
          ElMessage.error(i18n_t('proxyMgmt.pleaseLoginFirst'))
          router.push('/user-login?callback=proxy-mgmt')
          return
        }

        if (data.errCode !== 0) {
          throw new Error(data.errMsg || i18n_t('proxyMgmt.clearLogsFailed'))
        }

        ElMessage.success(i18n_t('proxyMgmt.logsCleared'))
        fetchLogs(true)
      } catch (error) {
        if (error !== 'cancel') {
          console.error(i18n_t('proxyMgmt.clearLogsFailed') + ':', error)
          ElMessage.error(i18n_t('proxyMgmt.clearLogsFailed') + ': ' + (error.message || i18n_t('proxyMgmt.unknownError')))
        }
      } finally {
        logClearing.value = false
      }
    }

    // 下载所有日志
    const downloadAllLogs = async () => {
      try {
        logDownloading.value = true

        // 获取所有日志（设置一个足够大的size值）
        const params = new URLSearchParams({
          module: 'proxy',
          page: 1,
          size: 10000 // 设置一个较大的数值，获取尽可能多的日志
        })

        const response = await fetch(`${API_BASE_URL}/get-moduleLog?${params.toString()}`, {
          credentials: 'include'
        })

        if (!response.ok) throw new Error(i18n_t('proxyMgmt.networkError'))

        const data = await response.json()
        if (data.errCode === 1000) {
          ElMessage.error(i18n_t('proxyMgmt.pleaseLoginFirst'))
          router.push('/user-login?callback=proxy-mgmt')
          return
        }

        if (data.errCode !== 0) {
          throw new Error(data.errMsg || i18n_t('proxyMgmt.fetchLogsFailed'))
        }

        const allLogs = data.data || []

        if (allLogs.length === 0) {
          ElMessage.warning(i18n_t('proxyMgmt.noLogsToDownload'))
          return
        }
        
        // 格式化所有日志内容
        let logContent = ''
        allLogs.forEach(log => {
          logContent += `[${log.Time}] [${log.Level}] ${log.Content}\n`
        })
        
        // 创建下载链接
        const blob = new Blob([logContent], { type: 'text/plain;charset=utf-8' })
        const url = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = `${module}_proxy_logs_${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.log`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        URL.revokeObjectURL(url)
        
        ElMessage.success(i18n_t('proxyMgmt.downloadSuccess', { count: allLogs.length }))
      } catch (error) {
        console.error(i18n_t('proxyMgmt.downloadFailed') + ':', error)
        ElMessage.error(i18n_t('proxyMgmt.downloadFailed') + ': ' + (error.message || i18n_t('proxyMgmt.unknownError')))
      } finally {
        logDownloading.value = false
      }
    }

    // 处理每页条数变化
    const handleLogSizeChange = (size) => {
      logPageSize.value = size
      logCurrentPage.value = 1
      fetchLogs(true)
    }

    // 处理页码变化
    const handleLogPageChange = (page) => {
      logCurrentPage.value = page
      fetchLogs(true)
    }

    // 获取代理数据
    const fetchProxyData = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/get-proxy`, {
          credentials: 'include'
        })

        if (!response.ok) {
          throw new Error(i18n_t('proxyMgmt.fetchDataFailed'))
        }

        const data = await response.json()
        if (data.errCode === 1000) {
          ElMessage.error(i18n_t('proxyMgmt.pleaseLoginFirst'))
          router.push('/user-login?callback=proxy-mgmt')
          return
        }

        if (data.errCode !== 0) {
          throw new Error(data.errMsg || i18n_t('proxyMgmt.fetchDataFailed'))
        }

        isAdmin.value = data.isAdmin
        isLogin.value = data.isLogin
        loginUsername.value = data.loginUsername
        socks5.value = data.socks5
        proxy.value = data.proxy
      } catch (error) {
        console.error(i18n_t('proxyMgmt.fetchDataFailed') + ':', error)
        ElMessage.error(i18n_t('proxyMgmt.fetchDataFailed') + ': ' + error.message)
      }
    }
    
    // 窗口大小变化处理
    const handleResize = () => {
      checkMobile()
    }
    
    // 初始化
    onMounted(() => {
      checkMobile()
      window.addEventListener('resize', handleResize)
      fetchProxyData()
      
      // 每10秒刷新一次数据
      const intervalId = setInterval(fetchProxyData, 10000)
      
      // 组件卸载时清除定时器和事件监听
      onUnmounted(() => {
        clearInterval(intervalId)
        window.removeEventListener('resize', handleResize)
        stopLogAutoRefresh()
      })
    })
    
    return {
      isMobile,
      isAdmin,
      isLogin,
      loginUsername,
      socks5,
      proxy,
      showSocks5Password,
      showHttpsPassword,
      dialogVisible,
      dialogType,
      dialogForm,
      dialogFormRef,
      formRules,
      getStatusClass,
      formatStatus,
      showEditDialog,
      submitProxyConfig,
      toggleProxy,
      socks5Loading,
      httpsLoading,
      dialogLoading,
      handleCloseDialog,
      // 日志相关
      logDialogVisible,
      logDialogType,
      showLogDialog,
      logLoading,
      logClearing,
      logDownloading,
      logsData,
      logTotal,
      logCurrentPage,
      logPageSize,
      logContentRef,
      autoRefreshEnabled,
      toggleAutoRefresh,
      refreshLogs,
      clearLogs,
      downloadAllLogs,
      handleLogSizeChange,
      handleLogPageChange
    }
  }
}
</script>

<style scoped lang="scss">
.proxy-management-container {
  padding: 0px;
  max-width: 100%;
  margin: 0 auto;
  width: 100%;
  box-sizing: border-box;
  
  .main-content {
    margin-top: 10px;
    margin-left: 10px;
    padding: 0px 0px;
    
    @media (max-width: 768px) {
      margin-top: 10px;
      margin-left: 0;
      padding: 0 12px;
      width: 100%;
      box-sizing: border-box;
    }
    
    @media (max-width: 480px) {
      padding: 0 8px;
    }
  }
  
  .proxy-card {
    background: #fff;
    border-radius: 8px;
    box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
    margin-bottom: 24px;
    overflow: hidden;
    width: 100%;
    box-sizing: border-box;
    
    .card-header {
      padding: 16px 20px;
      border-bottom: 1px solid #ebeef5;
      display: flex;
      justify-content: space-between;
      align-items: center;
      
      .card-title {
        font-size: 18px;
        font-weight: 600;
        color: #303133;
        margin: 0;
      }
      
      .card-actions {
        display: flex;
        gap: 8px;
      }
    }
    
    .card-body {
      padding: 20px;
      
      .description-text {
        font-size: 14px;
        color: #606266;
        margin: 0;
      }
    }
    
    @media (max-width: 768px) {
      margin-bottom: 16px;
      
      .card-header {
        padding: 12px 16px;
        flex-direction: column;
        align-items: flex-start;
        gap: 10px;
        
        .card-title {
          font-size: 16px;
        }
        
        .card-actions {
          width: 100%;
          justify-content: flex-end;
        }
        
        .el-button {
          align-self: flex-end;
        }
      }
      
      .card-body {
        padding: 12px;
        
        .description-text {
          font-size: 12px;
        }
      }
    }
    
    @media (max-width: 480px) {
      .card-header {
        padding: 10px 12px;
        
        .card-title {
          font-size: 15px;
        }
        
        .card-actions {
          .el-button--small {
            padding: 5px 8px;
            font-size: 11px;
          }
        }
      }
      
      .card-body {
        padding: 8px;
      }
    }
  }
  
  .table-responsive {
    overflow-x: auto;
    width: 100%;
    
    @media (max-width: 768px) {
      width: 100%;
      overflow-x: auto;
      -webkit-overflow-scrolling: touch;
    }
  }
  
  .proxy-table {
    width: 100%;
    border-collapse: collapse;
    
    th, td {
      padding: 10px 12px;
      text-align: left;
      border-bottom: 1px solid #ebeef5;
    }
    
    th {
      background-color: #f5f7fa;
      color: #909399;
      font-weight: 600;
    }
    
    td {
      color: #606266;
      vertical-align: middle;
    }
    
    @media (max-width: 768px) {
      width: 100%;
      min-width: 100%;
      display: block;
      
      thead {
        display: none;
      }
      
      tbody, tr {
        width: 100%;
        display: block;
      }
      
      tr {
        margin-bottom: 16px;
        border: 1px solid #ebeef5;
        border-radius: 4px;
        background: #fff;
      }
      
      td {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 8px 12px;
        border-bottom: 1px solid #ebeef5;
        width: 100%;
        box-sizing: border-box;
        
        &:before {
          content: attr(data-label);
          font-weight: 600;
          color: #909399;
          margin-right: 12px;
          flex-shrink: 0;
          min-width: 70px;
          font-size: 12px;
        }
        
        &:last-child {
          border-bottom: none;
        }
        
        &.action-cell {
          padding: 8px 12px;
          
          &:before {
            align-self: flex-start;
            padding-top: 4px;
          }
        }
      }
    }
    
    @media (max-width: 480px) {
      td {
        padding: 6px 8px;
        font-size: 12px;
        
        &:before {
          min-width: 60px;
          font-size: 11px;
        }
      }
    }
  }
  
  .status-badge {
    display: inline-block;
    padding: 4px 8px;
    border-radius: 4px;
    font-size: 12px;
    
    &.running {
      background-color: #f0f9eb;
      color: #67c23a;
    }
    
    &.stopped {
      background-color: #f5f5f5;
      color: #909399;
    }
    
    &.error {
      background-color: #fef0f0;
      color: #f56c6c;
    }
    
    @media (max-width: 768px) {
      padding: 3px 6px;
      font-size: 11px;
    }
  }
  
  .password-field {
    display: flex;
    align-items: center;
    gap: 8px;
    
    .eye-icon {
      cursor: pointer;
      color: #909399;
      font-size: 16px;
      
      &:hover {
        color: #409eff;
      }
    }
    
    @media (max-width: 768px) {
      .eye-icon {
        font-size: 14px;
      }
      
      span {
        max-width: 120px;
        overflow: hidden;
        text-overflow: ellipsis;
      }
    }
  }
  
  .action-cell {
    white-space: nowrap;
    
    .action-buttons {
      display: flex;
      gap: 4px;
      flex-wrap: nowrap;
      
      .el-button {
        flex-shrink: 0;
      }
      
      .el-button + .el-button {
        margin-left: 0;
      }
    }
    
    @media (max-width: 768px) {
      .action-buttons {
        justify-content: flex-end;
        
        .el-button--small {
          padding: 4px 8px;
          font-size: 12px;
        }
      }
    }
    
    @media (max-width: 480px) {
      .action-buttons {
        .el-button--small {
          padding: 3px 6px;
          font-size: 11px;
        }
      }
    }
  }
  
  /* 日志对话框样式 */
  .log-dialog {
    :deep(.el-dialog__body) {
      padding: 20px;
      max-height: 70vh;
      overflow-y: auto;
    }
  }
  
  .log-toolbar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;
    padding-bottom: 12px;
    border-bottom: 1px solid #ebeef5;
    
    .log-stats {
      display: flex;
      align-items: center;
      gap: 24px;
      font-size: 13px;
      color: #606266;
      
      .auto-refresh-control {
        display: flex;
        align-items: center;
        gap: 8px;
        padding-left: 16px;
        border-left: 1px solid #dcdfe6;
        
        .refresh-label {
          font-size: 12px;
          color: #909399;
        }
        
        .refresh-interval {
          font-size: 12px;
          color: #67c23a;
        }
      }
    }
    
    .log-actions {
      display: flex;
      gap: 8px;
    }
    
    @media (max-width: 768px) {
      flex-direction: column;
      gap: 12px;
      align-items: flex-start;
      
      .log-stats {
        width: 100%;
        flex-wrap: wrap;
        gap: 12px;
        
        .auto-refresh-control {
          padding-left: 0;
          border-left: none;
        }
      }
      
      .log-actions {
        width: 100%;
        justify-content: flex-end;
      }
    }
    
    @media (max-width: 480px) {
      .log-stats {
        font-size: 12px;
      }
    }
  }
  
  .log-content {
    border: 1px solid #ebeef5;
    border-radius: 4px;
    background-color: #fafafa;
    margin-bottom: 16px;
    max-height: 400px;
    overflow-y: auto;
    
    .empty-log {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      padding: 40px;
      color: #909399;
      
      .el-icon {
        font-size: 32px;
        margin-bottom: 12px;
      }
      
      p {
        margin: 0;
        font-size: 14px;
      }
    }
    
    .log-list {
      padding: 12px;
      
      .log-item {
        padding: 8px 12px;
        border-bottom: 1px solid #e4e7ed;
        font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
        font-size: 13px;
        line-height: 1.5;
        display: flex;
        align-items: flex-start;
        gap: 8px;
        
        &:last-child {
          border-bottom: none;
        }
        
        &.log-level-error {
          background-color: #fef0f0;
        }
        
        &.log-level-warn {
          background-color: #fdf6ec;
        }
        
        .log-time {
          color: #909399;
          font-size: 12px;
          white-space: nowrap;
          flex-shrink: 0;
        }
        
        .log-level {
          font-weight: 600;
          font-size: 12px;
          white-space: nowrap;
          flex-shrink: 0;
          
          &.level-error {
            color: #f56c6c;
          }
          
          &.level-warn {
            color: #e6a23c;
          }
          
          &.level-info {
            color: #409eff;
          }
        }
        
        .log-message {
          color: #303133;
          word-break: break-all;
          flex: 1;
        }
      }
    }
    
    @media (max-width: 768px) {
      max-height: 350px;
      
      .log-list {
        padding: 8px;
        
        .log-item {
          font-size: 12px;
          flex-wrap: wrap;
          gap: 4px;
          
          .log-time {
            width: 100%;
            margin-bottom: 2px;
          }
          
          .log-message {
            width: 100%;
            margin-left: 0;
          }
        }
      }
    }
    
    @media (max-width: 480px) {
      max-height: 300px;
      
      .log-list {
        .log-item {
          font-size: 11px;
        }
      }
    }
  }
  
  .log-pagination {
    display: flex;
    justify-content: flex-end;
    
    @media (max-width: 768px) {
      justify-content: center;
    }
  }
  
  .dialog-footer {
    display: flex;
    justify-content: flex-end;
    gap: 12px;
    
    @media (max-width: 480px) {
      flex-direction: column;
      
      .el-button {
        width: 100%;
        margin-left: 0 !important;
      }
    }
  }
}

/* Element Plus 对话框移动端适配 */
.proxy-dialog,
.settings-dialog {
  :deep(.el-dialog) {
    @media (max-width: 768px) {
      width: 95% !important;
      margin: 8px auto !important;
    }
    
    @media (max-width: 480px) {
      width: 100% !important;
      height: auto;
      margin: 0 !important;
      max-height: 100%;
      border-radius: 0;
      
      .el-dialog__header {
        padding: 12px 16px;
      }
      
      .el-dialog__body {
        padding: 16px;
        overflow-y: auto;
        max-height: calc(100vh - 120px);
      }
      
      .el-dialog__footer {
        padding: 12px 16px;
      }
    }
  }
  
  :deep(.el-form-item) {
    @media (max-width: 768px) {
      margin-bottom: 18px;
      
      .el-form-item__label {
        padding-bottom: 4px;
      }
    }
  }
  
  :deep(.el-input__inner) {
    @media (max-width: 768px) {
      font-size: 13px;
      height: 34px;
      line-height: 34px;
    }
    
    @media (max-width: 480px) {
      font-size: 12px;
      height: 32px;
      line-height: 32px;
    }
  }
  
  :deep(.el-switch) {
    @media (max-width: 768px) {
      .el-switch__core {
        width: 36px !important;
        height: 18px !important;
        
        &:after {
          width: 14px !important;
          height: 14px !important;
        }
      }
    }
  }
}

/* 全局按钮适配 */
:deep(.el-button--small) {
  @media (max-width: 768px) {
    padding: 6px 12px;
    font-size: 12px;
  }
  
  @media (max-width: 480px) {
    padding: 5px 10px;
    font-size: 11px;
  }
}
</style>